import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Twitter, Shield, Truck, Award, CreditCard, MessageCircle, Music2, Mail, Phone, Star, Zap, Package, CheckCircle, Lock, Gift, Clock, RotateCcw, HeartHandshake } from 'lucide-react';
import { BrandLogo, useSiteBrand } from './BrandLogo';

const BADGE_ICONS = {
  truck: Truck,
  shield: Shield,
  award: Award,
  card: CreditCard,
  star: Star,
  zap: Zap,
  package: Package,
  check: CheckCircle,
  lock: Lock,
  gift: Gift,
  phone: Phone,
  clock: Clock,
  returns: RotateCcw,
  handshake: HeartHandshake,
};

const DEFAULT_BADGES = [
  { i: 'truck', t: 'Envío gratis', d: 'En cualquier compra' },
  { i: 'shield', t: 'Garantía', d: 'Contra defectos de fábrica' },
  { i: 'award', t: '100% Original', d: 'Marcas certificadas' },
  { i: 'card', t: 'Pago seguro', d: 'Múltiples métodos' },
];

const SOCIALS = [
  { key: 'instagram_url', Icon: Instagram, label: 'Instagram' },
  { key: 'facebook_url', Icon: Facebook, label: 'Facebook' },
  { key: 'youtube_url', Icon: Youtube, label: 'YouTube' },
  { key: 'twitter_url', Icon: Twitter, label: 'X / Twitter' },
  { key: 'tiktok_url', Icon: Music2, label: 'TikTok' },
];

const Footer = () => {
  const brand = useSiteBrand();
  const siteName = brand.site_name || 'NubesMTY';
  const year = new Date().getFullYear();
  const copyright = (brand.footer_copyright || `${siteName} © {year}. Todos los derechos reservados. Producto para adultos 18+.`)
    .replaceAll('{year}', String(year))
    .replaceAll('{site}', siteName);
  const badges = Array.isArray(brand.footer_badges) && brand.footer_badges.length ? brand.footer_badges : DEFAULT_BADGES;
  const isValidUrl = (v) => {
    if (!v || typeof v !== 'string') return false;
    const t = v.trim();
    return /^https?:\/\/.+\..+/i.test(t);
  };
  const socials = SOCIALS
    .map((s) => ({ ...s, href: isValidUrl(brand[s.key]) ? brand[s.key].trim() : null }))
    .filter((s) => s.href);
  const waRaw = String(brand.whatsapp_number || '').replace(/\D/g, '');

  return (
    <footer className="mt-20 bg-black border-t border-white/10 text-white/80" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="mb-4">
              <BrandLogo size="md" />
            </div>
            <p className="text-sm leading-relaxed text-white/60">
              {brand.footer_note || 'La mejor tienda de vapes en México.'}
            </p>
            {(brand.contact_email || brand.contact_phone) && (
              <div className="mt-4 space-y-1.5">
                {brand.contact_email && (
                  <a href={`mailto:${brand.contact_email}`} className="flex items-center gap-2 text-xs text-white/60 hover:text-orange-400">
                    <Mail className="w-3.5 h-3.5" /> {brand.contact_email}
                  </a>
                )}
                {brand.contact_phone && (
                  <a href={`tel:${brand.contact_phone.replace(/\s+/g,'')}`} className="flex items-center gap-2 text-xs text-white/60 hover:text-orange-400">
                    <Phone className="w-3.5 h-3.5" /> {brand.contact_phone}
                  </a>
                )}
                {waRaw && (
                  <a href={`https://wa.me/${waRaw}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-white/60 hover:text-[#25D366]">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                )}
              </div>
            )}
          </div>

          <div>
            <h5 className="font-bold text-white mb-3">Ayuda</h5>
            <ul className="space-y-2 text-sm">
              <li><Link to="/preguntas-frecuentes" className="hover:text-orange-400">Preguntas frecuentes</Link></li>
              <li><Link to="/formas-de-pago" className="hover:text-orange-400">Formas de pago</Link></li>
              <li><Link to="/envios-y-devoluciones" className="hover:text-orange-400">Envíos y devoluciones</Link></li>
              <li><Link to="/contacto" className="hover:text-orange-400">Contacto</Link></li>
              <li><Link to="/terminos-del-servicio" className="hover:text-orange-400">Términos del servicio</Link></li>
              <li><Link to="/aviso-de-privacidad" className="hover:text-orange-400">Aviso de privacidad</Link></li>
            </ul>
          </div>

          <div>
            <h5 className="font-bold text-white mb-3">Síguenos</h5>
            {socials.length ? (
              <div className="flex items-center gap-2 flex-wrap">
                {socials.map((s) => {
                  const Ic = s.Icon;
                  return (
                    <a
                      key={s.key}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={s.label}
                      title={s.label}
                      className="w-9 h-9 rounded-full bg-white/5 hover:bg-orange-500 hover:text-black flex items-center justify-center transition-colors"
                    >
                      <Ic className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-white/40">Aún no hay redes configuradas.</p>
            )}
            {brand.footer_disclaimer && (
              <p className="mt-4 text-[11px] text-white/40 leading-relaxed">
                {brand.footer_disclaimer}
              </p>
            )}
          </div>
        </div>

        {badges.length > 0 && (
          <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {badges.map((b, i) => {
              const Ic = BADGE_ICONS[b.i] || Award;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center">
                    <Ic className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm">{b.t}</p>
                    <p className="text-xs text-white/50">{b.d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 text-center text-xs text-white/40">
          {copyright}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
